import Clarity from '@microsoft/clarity';

/**
 * Microsoft Clarity wrapper — session recordings + heatmaps.
 *
 * Pure: no React, no env reads. The app passes the build-time project id
 * (mirrors how `main.tsx` passes `VITE_SENTRY_DSN` to `Sentry.init`). Every
 * function no-ops when Clarity isn't configured/initialized, so callers never
 * need to guard. The project id is injected only by `deploy-prod.yml`
 * (GitHub repo var), so Clarity loads in production only.
 */
let initialized = false;

/** Loads the Clarity tag once. No-op without a project id or if already initialized. */
export function initClarity(projectId: string | undefined): void {
  if (!projectId || initialized) return;
  Clarity.init(projectId);
  // Without this signal Clarity stays in no-consent mode for EEA, UK and Swiss users.
  Clarity.consentV2({ ad_Storage: 'denied', analytics_Storage: 'granted' });
  initialized = true;
}

export interface ClarityUser {
  uid: string;
}

/**
 * Links the session to a user. Clarity SHA-256 hashes the uid. No-op until init.
 * Only the uid is sent: no email or name goes to Microsoft.
 */
export function identifyClarityUser({ uid }: ClarityUser): void {
  if (!initialized || !uid) return;
  Clarity.identify(uid);
}

export interface ClarityTags {
  role?: string;
  brandId?: string;
}

/**
 * Applies filterable custom tags to the session. No-op until init. Undefined
 * values are skipped — covers both "not applicable" (e.g. admins have no brandId)
 * and "not loaded yet" (scopes still resolving); the caller re-invokes on resolve.
 */
export function setClarityTags({ role, brandId }: ClarityTags): void {
  if (!initialized) return;
  if (role) Clarity.setTag('role', role);
  if (brandId) Clarity.setTag('brandId', brandId);
}
